import { useCallback, useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import {
  debounce,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import { useAuth } from "@/features/authentication";
import { OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";

const formatDateWithAge = (dateString) => {
  const date = dayjs(dateString);
  const today = dayjs();
  const diffDays = today.diff(date, "day");

  // Format: "DD MMM" (without year)
  const formattedDate = date.format("DD MMM");

  // If it's today, only show the date
  if (diffDays === 0) {
    return formattedDate;
  }

  // Otherwise, show date with age
  const ageLabel = diffDays === 1 ? "1d" : `${diffDays}d`;
  return `${formattedDate}, ${ageLabel}`;
};

export const Dashboard = () => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize outlet from URL params or null
  const [outlet, setOutlet] = useState(() => {
    const outletId = searchParams.get("outletId");
    if (outletId) {
      return user.outlets.find((o) => o.id === outletId) || null;
    }
    return null;
  });

  const [salesOrderLensProducts, setSalesOrderLensProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([
    { field: "salesOrderRetailTransactionDate", sort: "desc" },
  ]);
  const [undelivered, setUndelivered] = useState(true);

  const loadUnorderedSalesOrderLensProducts = useCallback(async () => {
    setLoading(true);
    try {
      const sortOrder =
        sortModel.length > 0 ? sortModel[0].sort.toUpperCase() : "DESC";

      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("retail-sales-order-lens-products", {
        params: {
          page: paginationModel.page,
          size: paginationModel.pageSize,
          referenceNumber: searchQuery || null,
          hasPurchaseOrder: false,
          outletId: outlet?.id,
          order: sortOrder,
          undelivered,
        },
      });
      setSalesOrderLensProducts(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memuat pesanan penjualan lensa pre-order")
      );
    } finally {
      setLoading(false);
    }
  }, [
    outlet?.id,
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    sortModel,
    undelivered,
    showSnackbar,
  ]);

  useEffect(() => {
    loadUnorderedSalesOrderLensProducts();
  }, [loadUnorderedSalesOrderLensProducts]);

  const handleSearchQueryChange = debounce((event) => {
    setSearchQuery(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, 500);

  const handleOutletChange = (newOutlet) => {
    setOutlet(newOutlet);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (newOutlet) {
      newParams.set("outletId", newOutlet.id);
    } else {
      newParams.delete("outletId");
    }
    setSearchParams(newParams);

    // Reset pagination when changing outlet
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <Stack spacing={3} sx={{
      p: 3
    }}>
      <Typography variant="h5">Status Lensa Pre-Order</Typography>
      <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={undelivered}
            onChange={(e) => {
              setUndelivered(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value={false}>Semua</MenuItem>
            <MenuItem value={true}>Belum Terkirim</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <UserOutletsSelect
            value={outlet}
            onChange={handleOutletChange}
            disableClearable={false}
            allowedRoles={[OutletRoleEnum.RETAIL]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <SearchTextField
            fullWidth
            placeholder="Cari No. SO Retail"
            onChange={handleSearchQueryChange}
          />
        </Grid>
      </Grid>
      <DataGrid
        rows={salesOrderLensProducts}
        loading={loading}
        getRowHeight={() => "auto"}
        columns={[
          {
            field: "salesOrderRetail",
            headerName: "SO Retail",
            flex: 1.5,
            sortable: false,
            valueGetter: (_, { salesOrderRetail: { referenceNumber } }) =>
              referenceNumber,
          },
          {
            field: "salesOrderRetailTransactionDate",
            headerName: "Tanggal",
            flex: 1,
            valueGetter: (_, { salesOrderRetail: { transactionDate } }) =>
              formatDateWithAge(transactionDate),
          },
          {
            field: "outletRetail",
            headerName: "Outlet Retail",
            flex: 1,
            sortable: false,
            valueGetter: (_, { salesOrderRetail: { seller } }) => seller.name,
          },
          {
            field: "customerRetail",
            headerName: "Pelanggan Retail",
            flex: 1,
            sortable: false,
            valueGetter: (_, { salesOrderRetail: { buyer } }) => buyer.name,
          },
          {
            field: "lensaAndVariant",
            headerName: "Lensa & Varian",
            flex: 3,
            sortable: false,
            renderCell: ({ row }) => {
              return (
                <Stack spacing={0.5}>
                  <Typography variant="body2">{row.name}</Typography>
                  {row.salesOrderLensProductVariants?.length ? (
                    row.salesOrderLensProductVariants.map((variant) => {
                      const sideLabel = variant.side === "LEFT" ? "L" : "R";

                      // Get axis from note
                      let axis = "-";
                      try {
                        const parsed = JSON.parse(variant.note || "{}");
                        axis = parsed.axis || "0";
                      } catch {
                        // Ignore JSON parse errors
                      }

                      const value1 = variant.variantAttribute1Value || "-";
                      const value2 = variant.variantAttribute2Value || "-";
                      const value3 = variant.variantAttribute3Value || "0"; // Use "0" if null

                      const prescription = `${value1}/${value2}/${axis}/${value3}`;

                      return (
                        <Typography key={variant.id} variant="body2">
                          {sideLabel}: {prescription}
                        </Typography>
                      );
                    })
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Stack>
              );
            },
          },
          {
            field: "purchaseOrderExternalReferenceNumber",
            headerName: "PO Eksternal",
            flex: 1.5,
            sortable: false,
            valueFormatter: (value) => value ?? "-",
          },
          {
            field: "purchaseOrderInternalReferenceNumber",
            headerName: "PO Internal",
            flex: 1.5,
            sortable: false,
            valueFormatter: (value) => value ?? "-",
          },
          {
            field: "salesRequestInternalReferenceNumber",
            headerName: "SR Internal",
            flex: 1.5,
            sortable: false,
            valueFormatter: (value) => value ?? "-",
          },
          {
            field: "salesOrderInternalReferenceNumber",
            headerName: "SO Internal",
            flex: 1.5,
            sortable: false,
            valueFormatter: (value) => value ?? "-",
          },
          {
            field: "deliveryOrderInternalReferenceNumber",
            headerName: "DO Internal",
            flex: 1.5,
            sortable: false,
            valueFormatter: (value) => value ?? "-",
          },
        ]}
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
      />
    </Stack>
  );
};
